"use client";

import { useCallback, useEffect, useState } from "react";
import { adminFetch } from "@/lib/admin-client";
import { Badge, Button, Card, Spinner, inputClass } from "./ui";

/**
 * 容器要素格式编辑器（三个容器页共用）。
 * 容器 = 记录格式的定义；用户真实数据是按此格式落库的实例。
 */

interface ElementSpec {
  key: string;
  label: string;
  type: string;
  required: boolean;
  provider?: string;
  description?: string;
}

interface ContainerSchema {
  kind: string;
  version: number;
  elements: ElementSpec[];
}

const TYPES = [
  "text",
  "number",
  "boolean",
  "image",
  "images",
  "video",
  "audio",
  "externalId",
  "json",
];
const PROVIDERS = [
  "",
  "heygen",
  "heygen_clone",
  "seedance",
  "kickart",
  "minimax_h3",
  "infinitetalk",
  "mock",
];

const EMPTY = {
  key: "",
  label: "",
  type: "text",
  provider: "",
  required: false,
  description: "",
};

export function SchemaEditor({ kind }: { kind: "product" | "avatar" | "voice" }) {
  const [schema, setSchema] = useState<ContainerSchema | null>(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setSchema(await adminFetch<ContainerSchema>(`/api/admin/schemas/${kind}`));
    } catch {
      // 401 由页面级 gate 处理，这里静默
    }
  }, [kind]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(elements: ElementSpec[]) {
    setBusy(true);
    setNotice(null);
    try {
      const updated = await adminFetch<ContainerSchema>(
        `/api/admin/schemas/${kind}`,
        { method: "PUT", body: JSON.stringify({ elements }) },
      );
      setSchema(updated);
      setNotice(`Saved — format v${updated.version}`);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  if (!schema) {
    return (
      <Card className="mt-6 grid place-items-center p-10 text-muted">
        <Spinner className="size-5" />
      </Card>
    );
  }

  return (
    <Card className="mt-6 p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">
            Recording format · v{schema.version}
          </div>
          <p className="mt-1 text-[12px] text-muted">
            Which elements get captured for every asset of this kind — the
            container defines the format; user data is stored as instances of
            it. Tag route-specific elements with a provider.
          </p>
        </div>
      </div>
      {notice && <div className="mt-3 text-[12px] text-muted">{notice}</div>}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-[13px]">
          <thead>
            <tr className="border-b border-line text-[11px] tracking-wide text-muted uppercase">
              <th className="py-2 pr-3">Key</th>
              <th className="py-2 pr-3">Label</th>
              <th className="py-2 pr-3">Type</th>
              <th className="py-2 pr-3">Route</th>
              <th className="py-2 pr-3">Required</th>
              <th className="py-2 pr-3"></th>
            </tr>
          </thead>
          <tbody>
            {schema.elements.map((el) => (
              <tr key={el.key} className="border-b border-line/60">
                <td className="py-2 pr-3 font-mono text-[12px]">{el.key}</td>
                <td className="py-2 pr-3">
                  {el.label}
                  {el.description && (
                    <div className="text-[11px] text-muted">
                      {el.description}
                    </div>
                  )}
                </td>
                <td className="py-2 pr-3">
                  <Badge>{el.type}</Badge>
                </td>
                <td className="py-2 pr-3">
                  {el.provider ? (
                    <Badge tone="accent">{el.provider}</Badge>
                  ) : (
                    <span className="text-[11px] text-muted">common</span>
                  )}
                </td>
                <td className="py-2 pr-3">{el.required ? "✓" : "—"}</td>
                <td className="py-2 pr-3 text-right">
                  <button
                    type="button"
                    className="text-[12px] text-muted transition hover:text-[var(--danger)]"
                    disabled={busy}
                    onClick={() =>
                      void save(
                        schema.elements.filter((x) => x.key !== el.key),
                      )
                    }
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 grid gap-2 border-t border-line pt-4 sm:grid-cols-6">
        <input
          className={inputClass}
          placeholder="key"
          value={form.key}
          onChange={(e) => setForm({ ...form, key: e.target.value })}
        />
        <input
          className={inputClass}
          placeholder="Label"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
        />
        <select
          className={inputClass}
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
        >
          {TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          className={inputClass}
          value={form.provider}
          onChange={(e) => setForm({ ...form, provider: e.target.value })}
        >
          {PROVIDERS.map((p) => (
            <option key={p} value={p}>
              {p || "common"}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-[12px] text-muted">
          <input
            type="checkbox"
            checked={form.required}
            onChange={(e) => setForm({ ...form, required: e.target.checked })}
          />
          required
        </label>
        <Button
          size="sm"
          disabled={busy || !form.key}
          onClick={() => {
            void save([
              ...schema.elements,
              {
                key: form.key,
                label: form.label || form.key,
                type: form.type,
                required: form.required,
                provider: form.provider || undefined,
              },
            ]).then(() => setForm({ ...EMPTY }));
          }}
        >
          {busy ? <Spinner /> : "Add element"}
        </Button>
      </div>
    </Card>
  );
}
